import { Instance, SnapshotIn, types } from "mobx-state-tree";
import { SurveyRuleConditionValidationError } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRuleConditionValidationError";
import {
  getSurveyRuleValidationErrorValues,
  SurveyRuleCommonValidationError,
} from "../../utils/getSurveyRuleValidationErrorValues";

type ConditionKeyValidationError = Extract<
  SurveyRuleConditionValidationError.SurveyRuleConditionValidationError,
  | SurveyRuleConditionValidationError.SurveyRuleConditionValidationError.INVALID_QUESTION_KEY
  | SurveyRuleConditionValidationError.SurveyRuleConditionValidationError.INVALID_QUESTION_TYPE
  | SurveyRuleConditionValidationError.SurveyRuleConditionValidationError.INVALID_QUESTION_KEY_GROUP_CHILD
>;

const conditionKeyValidationErrorValues = Object.values(
  SurveyRuleConditionValidationError.SurveyRuleConditionValidationError,
).filter((validationError): validationError is ConditionKeyValidationError =>
  [
    SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
      .INVALID_QUESTION_KEY,
    SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
      .INVALID_QUESTION_TYPE,
    SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
      .INVALID_QUESTION_KEY_GROUP_CHILD,
  ].includes(validationError),
);

type ConditionValueValidationError = Extract<
  SurveyRuleConditionValidationError.SurveyRuleConditionValidationError,
  SurveyRuleConditionValidationError.SurveyRuleConditionValidationError.INVALID_OPTION_CODE_VALUE
>;

const conditionValueValidationErrorValues = Object.values(
  SurveyRuleConditionValidationError.SurveyRuleConditionValidationError,
).filter((validationError): validationError is ConditionValueValidationError =>
  [
    SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
      .INVALID_OPTION_CODE_VALUE,
  ].includes(validationError),
);

export const SurveyRuleQuestionKeyTypeModel = types
  .model("SurveyRuleQuestionKeyTypeModel", {
    type: types.literal("QUESTION"),
    conditionKey: types.maybeNull(types.string),
    conditionKeyValidationError: types.array(
      types.enumeration(
        "conditionKeyValidationError",
        getSurveyRuleValidationErrorValues(conditionKeyValidationErrorValues),
      ),
    ),
    conditionValue: types.array(types.string),
    conditionValueValidationError: types.array(
      types.enumeration(
        "conditionValueValidationError",
        getSurveyRuleValidationErrorValues(conditionValueValidationErrorValues),
      ),
    ),
  })
  .actions((store) => ({
    setConditionKey: (conditionKey: string | undefined): void => {
      store.conditionKeyValidationError.clear();
      store.conditionValueValidationError.clear();
      store.conditionKey = conditionKey ?? null;
      store.conditionValue.clear();
    },
    setConditionValue: (conditionValue: string[]): void => {
      store.conditionValueValidationError.clear();
      if (!conditionValue.length) {
        store.conditionValueValidationError.push(
          SurveyRuleCommonValidationError.EMPTY_VALUE,
        );
      }

      store.conditionValue.replace(conditionValue);
    },
    clearValidationErrors: (): void => {
      store.conditionKeyValidationError.clear();
      store.conditionValueValidationError.clear();
    },
    clearConditionKeyValidationErrors: (): void => {
      store.conditionKeyValidationError.clear();
    },
    setConditionKeyValidationError: (
      validationError: ConditionKeyValidationError,
    ): void => {
      store.conditionKeyValidationError.clear();
      store.conditionKeyValidationError.push(validationError);
    },
    setValidationError: (
      validationError:
        | ConditionKeyValidationError
        | ConditionValueValidationError,
    ): void => {
      switch (validationError) {
        case SurveyRuleConditionValidationError
          .SurveyRuleConditionValidationError.INVALID_QUESTION_KEY:
        case SurveyRuleConditionValidationError
          .SurveyRuleConditionValidationError.INVALID_QUESTION_TYPE:
        case SurveyRuleConditionValidationError
          .SurveyRuleConditionValidationError.INVALID_QUESTION_KEY_GROUP_CHILD:
          store.conditionKeyValidationError.push(validationError);
          break;
        case SurveyRuleConditionValidationError
          .SurveyRuleConditionValidationError.INVALID_OPTION_CODE_VALUE:
          store.conditionValueValidationError.push(validationError);
          break;
        default:
          validationError satisfies never;
      }
    },
  }));

export const createSurveyRuleQuestionKeyTypeModel = (
  surveyRuleQuestionKeyType?: SnapshotIn<typeof SurveyRuleQuestionKeyTypeModel>,
): Instance<typeof SurveyRuleQuestionKeyTypeModel> =>
  SurveyRuleQuestionKeyTypeModel.create(
    surveyRuleQuestionKeyType ?? {
      type: "QUESTION",
      conditionKey: null,
      conditionKeyValidationError: [],
      conditionValue: [],
      conditionValueValidationError: [],
    },
  );
